import React, { useState } from 'react';
import { QrCode, ChevronRight, Printer, ScrollText } from 'lucide-react';
import { POSTER_THEMES, POSTER_TOTAL, findPoster } from '../data/posterThemes';
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  PosterScaler,
  PosterSheet,
  PosterSheetStyles,
  type PosterQr,
} from './PosterSheet';

/**
 * Claude Design 由来の LP 後半セクション（安全ポスト＋ LP.dc.html）を React 化したもの。
 * - LandingDemoGuide: ダークネイビーの「使い方ガイド」。4ステップのタブ切替 + 全ステップ共通の
 *   スマホフレーム（サイズ固定）+ ガター内コールアウト。旧 how-it-works/PDCA/4Mデモ誘導を一本化。
 * - LandingPosters: 安全掲示ポスター（/posters）への導線。実物のA4紙面を縮小して見せる。
 * - LandingClosingCTA: オレンジグラデの最終CTA。
 * - LandingChangelog: ダークのタイムライン更新履歴（v1.0 + ロードマップ）。
 * いずれもブランドトークン safety(#f97316/#ea580c) × industrial(#0f172a) に準拠。
 */

type CalloutSide = 'left' | 'right';
interface Callout { n: number; text: string; side: CalloutSide; top: string }
interface DemoStep {
  id: number;
  badge: string;
  tab: string;
  title: string;
  desc: string;
  checklist: string[];
  callouts: Callout[];
}

const calloutStyle = (side: CalloutSide, top: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 7,
  position: 'absolute',
  zIndex: 4,
  width: 118,
  top,
  ...(side === 'left' ? { right: '100%', marginRight: 12 } : { left: '100%', marginLeft: 12 }),
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 11,
  padding: '8px 10px',
  boxShadow: '0 12px 26px -12px rgba(15,23,42,.45)',
});

const STEPS: DemoStep[] = [
  {
    id: 1,
    badge: 'STEP 1',
    tab: 'QR発行',
    title: '管理者：エリア別にQRコードを発行',
    desc: '現場やエリアごとにQRコードを発行し、印刷して掲示するだけ。登録からQR発行まで最短5分で導入できます。',
    checklist: ['エリア／工程別にQRを発行', 'PDFで印刷してそのまま現場に掲示', 'フリープランでもQR3個まで作成可'],
    callouts: [
      { n: 1, text: 'エリア別にQRを発行', side: 'left', top: '14%' },
      { n: 2, text: '印刷して現場に掲示', side: 'right', top: '62%' },
    ],
  },
  {
    id: 2,
    badge: 'STEP 2',
    tab: '報告',
    title: '作業員：QRを読んで30秒で報告',
    desc: '貼り出されたQRをスマホの標準カメラで読むだけ。アプリ不要・ログイン不要・完全匿名。テキスト／音声／写真で入力でき、多言語にも対応します。',
    checklist: ['アプリのインストール不要（Webアプリ）', '名前の入力は不要・完全匿名', '外国人作業員向けに多言語入力に対応'],
    callouts: [
      { n: 1, text: 'QRを読むだけ・アプリ不要', side: 'left', top: '10%' },
      { n: 2, text: '名前は不要・完全匿名', side: 'right', top: '42%' },
      { n: 3, text: '写真・音声でも報告', side: 'left', top: '74%' },
    ],
  },
  {
    id: 3,
    badge: 'STEP 3',
    tab: 'AI分析',
    title: 'AIが匿名化・4M分類・危険度判定',
    desc: '送信された報告をAIが解析。個人を特定する情報を自動で匿名化し、4Mで分類、危険度（High／Medium／Low）を判定して改善提案・教訓まで提示します。',
    checklist: ['個人特定情報を自動で匿名化', 'Man／Machine／Material／Method に自動分類', '危険度を判定し改善提案・教訓を抽出'],
    callouts: [
      { n: 1, text: '個人名をAIが自動で匿名化', side: 'left', top: '12%' },
      { n: 2, text: '4Mで自動分類', side: 'right', top: '46%' },
      { n: 3, text: '危険度を判定', side: 'left', top: '76%' },
    ],
  },
  {
    id: 4,
    badge: 'STEP 4',
    tab: '可視化',
    title: '管理画面で集計・改善を追跡',
    desc: '集まった報告は管理画面で自動集計。4M別・危険度別の偏りや対応状況を可視化し、改善のPDCAを回します。',
    checklist: ['4M別・危険度別に自動集計', '対応状況をステータスで追跡', '現場ごとの傾向を比較'],
    callouts: [
      { n: 1, text: '報告が集まり可視化', side: 'left', top: '18%' },
      { n: 2, text: '改善状況を追跡', side: 'right', top: '60%' },
    ],
  },
];

const QR_AREAS = [
  { name: 'A棟 入口ゲート', code: 'AP-A01' },
  { name: 'B棟 2F 作業床', code: 'AP-B02' },
  { name: '屋外 資材置場', code: 'AP-Y07' },
];

const DASH_BARS = [
  { label: '人', w: '52%' },
  { label: '機械', w: '84%' },
  { label: '材料', w: '40%' },
  { label: '方法', w: '66%' },
];

const CheckMark: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** スマホ画面の中身（ステップ別）。フレームは共通なので中身だけ差し替える。 */
const PhoneScreen: React.FC<{ step: number }> = ({ step }) => {
  if (step === 1) {
    return (
      <div style={{ height: '100%', padding: 16, background: '#f8fafc' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>QRコード管理</div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>エリア別に発行・印刷</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {QR_AREAS.map((q) => (
            <div key={q.code} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '9px 11px' }}>
              <div style={{ flex: '0 0 auto', width: 36, height: 36, borderRadius: 9, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <QrCode className="h-5 w-5" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{q.name}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{q.code}</div>
              </div>
              <span style={{ flex: '0 0 auto', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700, color: '#c2410c', background: '#fff7ed', borderRadius: 7, padding: '5px 9px' }}>印刷</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>フリープランは QR 3個まで</div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div style={{ height: '100%', padding: 16, background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: '#c2410c', background: '#fff7ed', borderRadius: 999, padding: '4px 10px', marginBottom: 12 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a' }} />匿名で報告中
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>どんな「ヒヤリ」でしたか？</div>
        <div style={{ flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 11, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>3F足場で手すりが一部外れていて、荷を運ぶ時に体が外側へふらついた。</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#0f172a', background: '#f1f5f9', borderRadius: 9, padding: '9px 0' }}>✏️ テキスト</span>
          <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 0' }}>🎤 音声</span>
          <span style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 0' }}>📷 写真</span>
        </div>
        <div style={{ marginTop: 12, height: 46, borderRadius: 12, background: 'linear-gradient(90deg,#f97316,#eab308)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 800 }}>30秒で送信する</div>
        <div style={{ textAlign: 'center', fontSize: 10, color: '#94a3b8', marginTop: 8 }}>名前の入力は不要・多言語対応</div>
      </div>
    );
  }
  if (step === 3) {
    return (
      <div style={{ height: '100%', padding: 16, background: '#f8fafc', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>AI分析結果</span>
          <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: '#dc2626', borderRadius: 999, padding: '4px 11px' }}>危険度 High</span>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 11, marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', marginBottom: 5 }}>✓ 匿名化済みの報告</div>
          <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>「<span style={{ background: '#0f172a', color: '#0f172a', borderRadius: 3, padding: '0 4px' }}>●●●</span>が3F足場で、手すりの欠落により体勢を崩した」</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 11, marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 7 }}>4M分類</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 9px' }}>人</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#c2410c', background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 7, padding: '4px 9px' }}>機械・設備</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 9px' }}>材料</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 9px' }}>方法</span>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 11 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>改善提案</div>
          <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>作業前点検に手すり固定の確認項目を追加。是正まで該当区画を立入制限。</div>
        </div>
      </div>
    );
  }
  // step 4
  return (
    <div style={{ height: '100%', padding: 16, background: '#f1f5f9', overflow: 'hidden' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>安全ダッシュボード</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 9 }}><div style={{ fontSize: 9, color: '#64748b' }}>今月の報告</div><div style={{ fontSize: 19, fontWeight: 800, color: '#0f172a' }}>42</div></div>
        <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 9 }}><div style={{ fontSize: 9, color: '#64748b' }}>High</div><div style={{ fontSize: 19, fontWeight: 800, color: '#dc2626' }}>5</div></div>
        <div style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 9 }}><div style={{ fontSize: 9, color: '#64748b' }}>対応済</div><div style={{ fontSize: 19, fontWeight: 800, color: '#16a34a' }}>37</div></div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 9 }}>4M別の報告</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {DASH_BARS.map((b) => (
            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: '0 0 38px', fontSize: 10, color: '#475569' }}>{b.label}</span>
              <span style={{ flex: 1, height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden', display: 'block' }}>
                <span style={{ display: 'block', height: '100%', borderRadius: 4, background: '#f97316', width: b.w }} />
              </span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 11, display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a' }} />
        <span style={{ fontSize: 11, color: '#475569' }}>改善のPDCAを継続中</span>
      </div>
    </div>
  );
};

export const LandingDemoGuide: React.FC<{ onTryWhyDemo: () => void }> = ({ onTryWhyDemo }) => {
  const [active, setActive] = useState(1);
  const step = STEPS.find((s) => s.id === active) || STEPS[0];
  const goNext = () => setActive((a) => (a >= 4 ? 1 : a + 1));

  return (
    <div style={{ padding: '0 clamp(8px,2vw,16px)', maxWidth: 1296, margin: '0 auto' }}>
      {/* コールアウトはガター（電話の外側）に出るため、狭い画面では隠す */}
      <style>{`@media (max-width:680px){[data-lp-callout]{display:none !important}}`}</style>
      <section
        id="guide"
        style={{ padding: 'clamp(40px,6vw,80px) clamp(16px,4vw,40px)', background: '#0f172a', borderRadius: 'clamp(20px,3vw,32px)', marginBottom: 'clamp(40px,6vw,72px)' }}
      >
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto clamp(24px,3vw,40px)' }}>
          <div style={{ display: 'inline-block', fontSize: 12, fontWeight: 800, letterSpacing: '.1em', color: '#fdba74', textTransform: 'uppercase', marginBottom: 10 }}>使い方ガイド</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 'clamp(1.6rem,3.2vw,2.4rem)', fontWeight: 800, letterSpacing: '-.02em', color: '#fff' }}>4ステップで、報告がAIで価値に変わる</h2>
          <p style={{ margin: 0, fontSize: 'clamp(.95rem,1.4vw,1.1rem)', color: '#cbd5e1' }}>QR発行から可視化まで。同じ画面で流れを確かめてください。</p>
        </div>

        {/* タブ */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 'clamp(24px,3vw,40px)' }}>
          {STEPS.map((s) => {
            const on = s.id === active;
            return (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                style={{
                  cursor: 'pointer', borderRadius: 11, padding: '11px 18px', fontSize: 14, fontWeight: 700,
                  transition: 'all .18s ease', border: '1px solid',
                  ...(on
                    ? { background: '#f97316', color: '#fff', borderColor: '#f97316', boxShadow: '0 10px 22px -10px rgba(249,115,22,.7)' }
                    : { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', borderColor: 'rgba(255,255,255,.14)' }),
                }}
              >
                <span style={{ opacity: 0.7, fontSize: 11, fontWeight: 800, marginRight: 7 }}>0{s.id}</span>{s.tab}
              </button>
            );
          })}
        </div>

        {/* 2カラム本体 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'clamp(24px,4vw,56px)', maxWidth: 1040, margin: '0 auto' }}>
          {/* 左: 説明 */}
          <div style={{ flex: '1 1 320px', minWidth: 300, minHeight: 430 }}>
            <span style={{ display: 'inline-block', fontSize: 13, fontWeight: 800, color: '#fdba74', background: 'rgba(249,115,22,.15)', border: '1px solid rgba(249,115,22,.3)', borderRadius: 999, padding: '5px 14px', marginBottom: 16 }}>{step.badge}</span>
            <h3 style={{ margin: '0 0 14px', fontSize: 'clamp(1.3rem,2.4vw,1.8rem)', fontWeight: 800, color: '#fff', lineHeight: 1.3 }}>{step.title}</h3>
            <p style={{ margin: '0 0 22px', fontSize: 15, color: '#cbd5e1', lineHeight: 1.8 }}>{step.desc}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {step.checklist.map((c) => (
                <li key={c} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#e2e8f0' }}>
                  <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: 'rgba(16,185,129,.18)', color: '#34d399', marginTop: 1 }}>
                    <CheckMark />
                  </span>
                  <span style={{ lineHeight: 1.6 }}>{c}</span>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button
                data-cta="guide-next-step"
                onClick={goNext}
                className="hover:bg-[#ea580c] transition"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 22px', border: 'none', cursor: 'pointer', borderRadius: 12, fontSize: 15, fontWeight: 800, color: '#fff', background: '#f97316', boxShadow: '0 12px 26px -10px rgba(249,115,22,.6)' }}
              >
                次のステップへ
                <ChevronRight className="h-[17px] w-[17px]" />
              </button>
              <button
                data-cta="try-why-demo"
                onClick={onTryWhyDemo}
                className="hover:bg-white/20 transition"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 15, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.18)', cursor: 'pointer' }}
              >
                4Mなぜなぜ分析を試す
              </button>
            </div>
          </div>

          {/* 右: 共通スマホステージ + コールアウト */}
          <div style={{ flex: '1 1 360px', minWidth: 300, display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: 'min(480px,100%)' }}>
              <div style={{ position: 'relative', zIndex: 1, width: 214, margin: '0 auto', background: 'linear-gradient(180deg,#1e293b,#0f172a)', borderRadius: 38, padding: 10, boxShadow: '0 40px 70px -24px rgba(0,0,0,.6)' }}>
                {/* コールアウト（電話の縁にアンカー） */}
                {step.callouts.map((co) => (
                  <div key={co.n} data-lp-callout={co.n} style={calloutStyle(co.side, co.top)}>
                    <span style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: '#f97316', color: '#fff', fontSize: 11, fontWeight: 800 }}>{co.n}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', lineHeight: 1.35 }}>{co.text}</span>
                  </div>
                ))}
                {/* ノッチ */}
                <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 78, height: 20, background: '#0f172a', borderRadius: '0 0 13px 13px', zIndex: 3 }} />
                <div style={{ background: '#f8fafc', borderRadius: 30, overflow: 'hidden', height: 430, display: 'flex', flexDirection: 'column' }}>
                  {/* ステータスバー */}
                  <div style={{ flex: '0 0 auto', height: 38, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 22px', background: '#fff', borderBottom: '1px solid #f1f5f9', fontSize: 12, fontWeight: 600, color: '#475569' }}>
                    <span>9:41</span>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{ width: 16, height: 9, border: '1.5px solid #94a3b8', borderRadius: 2, display: 'inline-block' }} />
                    </span>
                  </div>
                  <div style={{ flex: '1 1 auto', overflow: 'hidden', position: 'relative' }}>
                    <PhoneScreen step={active} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// 安全掲示ポスター（/posters への導線）
// ──────────────────────────────────────────────────────────────

/* LPに載せる実物見本。4テーマから1枚ずつ、絵柄のあるバリアントを選ぶ。
   図解型（H7/F5/C5/T5）は縮小すると線しか見えず「掲示物らしさ」が伝わらないので使わない。 */
const POSTER_PICKS = ['H1', 'F1', 'C1', 'T1'] as const;

/** サムネイル1枚の下限幅。段組みの計算だけに使う（ギャラリーより小さめ＝LPでは一覧性を優先）。 */
const LP_THUMB_MIN_WIDTH = 176;

interface PosterReason {
  Icon: React.ComponentType<{ size?: number | string; className?: string }>;
  title: string;
  body: string;
}

/* 訴求の順番には意味がある。
   ① いま現場が実際に負っている作業（毎月の掲示物づくり）を肩代わりすると言う
   ② なぜ今それが重い仕事になったのか（2025年6月の熱中症の周知義務）を示す
   ③ そのうえで、掲示物が報告の入口になるという本製品固有の価値に接続する
   ①→②で「自分の仕事の話だ」と分かってから③を読ませる。逆順だと売り込みから始まる。 */
const POSTER_REASONS: readonly PosterReason[] = [
  {
    Icon: Printer,
    title: '「作る」を「選ぶ」に置き換える',
    body: '安全掲示物は、標語を考え、体裁を整え、刷り上がるまでが仕事です。ここではデザインを選び、会社名と現場名を入れるだけ。A4で印刷して、そのまま掲示できます。',
  },
  {
    Icon: ScrollText,
    title: '熱中症は「周知」が義務になりました',
    body: '熱中症のおそれがある作業では、発症者を発見した際の報告体制と連絡先を定め、関係作業者へ周知することが事業者の義務とされています（労働安全衛生規則の一部を改正する省令／2025年6月1日施行）。紙面には緊急連絡先の記入欄と、根拠となる法令を載せています。',
  },
  {
    Icon: QrCode,
    title: '貼った紙が、報告の入口になる',
    body: '紙面のQRを読むと、その場で匿名のヒヤリハットを送れます。届いた報告はAIが匿名化して4Mで分類。登録なしでも印刷できます（QRは見本と印字）。実際に報告できるQRの発行には無料登録が必要です。',
  },
];

/** 親要素の実幅。ギャラリーの useElementWidth と同じ考え方だが、
 *  出荷済みの /posters 実装には手を入れない方針なので共有せずここに持つ。 */
function useRowWidth() {
  const [el, setEl] = React.useState<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return { ref: setEl, width };
}

/**
 * 安全掲示ポスターのLPセクション。
 *
 * 実物のA4紙面（PosterSheet）をそのまま縮小して並べる。画像だけを切り出して見せると
 * 「イラスト集」に見えてしまい、緊急連絡先の記入欄・QR枠・根拠法令まで入った“掲示物”
 * であることが伝わらない。仕上がりと見た目がずれないのも縮小表示の利点。
 *
 * 印刷時は PosterSheetStyles の @media print が .no-print を消すので、
 * LPを印刷してもここのA4紙面が等倍で紛れ込むことはない。
 */
export const LandingPosters: React.FC<{ onOpenPosters: () => void }> = ({ onOpenPosters }) => {
  const { ref: gridRef, width: gridWidth } = useRowWidth();

  const picks = React.useMemo(
    () => POSTER_PICKS.map((id) => findPoster(id)).filter((p): p is NonNullable<typeof p> => p !== null),
    [],
  );

  // 未ログインのLP来訪者に見せる紙面なので、QRは常に見本。
  // PosterSheet 側が 'sample' のとき紙面に「見本」と刻印する。
  const qr: PosterQr = React.useMemo(
    () => ({
      kind: 'sample',
      url: `${typeof window === 'undefined' ? 'https://anzenpost.com' : window.location.origin}/?ref=lp-poster-sample`,
    }),
    [],
  );

  // 狭い画面で1列にすると1枚が画面いっぱいになり、4テーマを見比べられない。2列に落とす。
  const isNarrow = gridWidth > 0 && gridWidth < 520;
  const gap = isNarrow ? 12 : 20;
  const columns = isNarrow
    ? 2
    : Math.max(2, Math.min(picks.length, Math.floor((gridWidth + gap) / (LP_THUMB_MIN_WIDTH + gap))));
  const cellWidth = gridWidth > 0 ? (gridWidth - gap * (columns - 1)) / columns : LP_THUMB_MIN_WIDTH;
  const thumbScale = cellWidth / A4_WIDTH_PX;

  // テーマ別の種類数はデータから出す（「全22種」を手書きすると増減で嘘になる）。
  const breakdown = POSTER_THEMES.map((t) => `${t.label}${t.variants.length}`).join('／');

  return (
    <section
      id="posters"
      className="no-print"
      style={{ padding: '0 clamp(16px,4vw,40px) clamp(40px,6vw,72px)', maxWidth: 1296, margin: '0 auto' }}
    >
      <PosterSheetStyles />

      {/* 紙は白いので、台紙は slate-100。ダークな使い方ガイドとオレンジの最終CTAの間で、
          「机の上に刷り上がった紙が並んでいる」トーンに切り替える。 */}
      <div
        style={{
          background: '#f1f5f9',
          border: '1px solid #e2e8f0',
          borderRadius: 'clamp(20px,3vw,28px)',
          padding: 'clamp(28px,4vw,48px) clamp(20px,4vw,44px)',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto clamp(24px,3vw,36px)' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '.08em',
              color: '#c2410c',
              background: '#fff7ed',
              border: '1px solid #ffedd5',
              borderRadius: 999,
              padding: '6px 14px',
              marginBottom: 14,
            }}
          >
            <Printer className="h-[13px] w-[13px]" />
            無料・登録なしで印刷できます
          </div>
          <h2
            style={{
              margin: '0 0 12px',
              fontSize: 'clamp(1.5rem,3vw,2.3rem)',
              fontWeight: 800,
              letterSpacing: '-.02em',
              color: '#0f172a',
              lineHeight: 1.25,
            }}
          >
            毎月の安全掲示物、
            <br className="sm:hidden" />
            ここで刷ってください。
          </h2>
          <p style={{ margin: 0, fontSize: 'clamp(.95rem,1.4vw,1.1rem)', color: '#475569', lineHeight: 1.75 }}>
            熱中症・墜落・はさまれ・転倒＋業種を問わない汎用の5テーマ、<strong style={{ color: '#0f172a', fontWeight: 700 }}>全{POSTER_TOTAL}種</strong>。
            会社名と緊急連絡先を入れて、A4で印刷するだけ。連絡先は空欄で刷って現場で手書きもできます（災害別の4テーマには根拠法令つき）。
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>{breakdown}（単位：種）</p>
        </div>

        {/* 実物見本。クリックでギャラリーへ。 */}
        <div
          ref={gridRef}
          style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }}
        >
          {picks.map(({ theme, variant }) => (
            <figure
              key={variant.id}
              role="button"
              tabIndex={0}
              aria-label={`${theme.label}のポスターを見る`}
              onClick={onOpenPosters}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenPosters();
                }
              }}
              className="group cursor-pointer transition hover:-translate-y-1"
              style={{ margin: 0, display: 'flex', flexDirection: 'column' }}
            >
              {/* 紙面そのものを縮小。文字は読めないので、下に標語を実テキストで併記する。 */}
              <div
                aria-hidden="true"
                style={{
                  width: '100%',
                  height: A4_HEIGHT_PX * thumbScale,
                  overflow: 'hidden',
                  borderRadius: 6,
                  background: '#fff',
                  boxShadow: '0 14px 30px -14px rgba(15,23,42,.45)',
                }}
              >
                <PosterScaler scale={thumbScale}>
                  <PosterSheet
                    theme={theme}
                    variant={variant}
                    orgName=""
                    siteName=""
                    qr={qr}
                    lazyImage
                  />
                </PosterScaler>
              </div>
              <figcaption style={{ marginTop: 10 }}>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#c2410c',
                    background: '#fff7ed',
                    border: '1px solid #ffedd5',
                    borderRadius: 999,
                    padding: '3px 9px',
                  }}
                >
                  {theme.label}
                </span>
                <span
                  style={{
                    display: 'block',
                    marginTop: 6,
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#334155',
                    lineHeight: 1.5,
                  }}
                >
                  {variant.s2.map((line) => line.map((seg) => seg.t).join('')).join('')}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* なぜこれが要るのか。 */}
        <div
          style={{
            marginTop: 'clamp(28px,4vw,44px)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(248px,1fr))',
            gap: 16,
          }}
        >
          {POSTER_REASONS.map((r) => (
            <div
              key={r.title}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 16,
                padding: 20,
              }}
            >
              <span
                className="inline-flex items-center justify-center"
                style={{ width: 40, height: 40, borderRadius: 11, background: '#fff7ed', color: '#ea580c', marginBottom: 12 }}
              >
                <r.Icon size={20} />
              </span>
              <h3 style={{ margin: '0 0 7px', fontSize: 16, fontWeight: 800, color: '#0f172a', lineHeight: 1.4 }}>
                {r.title}
              </h3>
              <p style={{ margin: 0, fontSize: 13.5, color: '#475569', lineHeight: 1.8 }}>{r.body}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 'clamp(24px,3vw,32px)', textAlign: 'center' }}>
          <button
            data-cta="lp-posters"
            onClick={onOpenPosters}
            className="inline-flex items-center hover:-translate-y-0.5 transition"
            style={{
              gap: 9,
              // 狭い画面でラベルが2行に折れないよう、字送りと内側余白を絞る。
              padding: '15px clamp(16px,4vw,28px)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 13,
              fontSize: 'clamp(14px,3.4vw,16px)',
              fontWeight: 800,
              color: '#fff',
              background: '#f97316',
              boxShadow: '0 14px 30px -10px rgba(249,115,22,.55)',
            }}
          >
            <Printer className="h-[18px] w-[18px]" />
            ポスターを見る（全{POSTER_TOTAL}種・無料）
            <ChevronRight className="h-[18px] w-[18px]" />
          </button>
          <p style={{ margin: '12px 0 0', fontSize: 13, color: '#64748b' }}>
            登録不要・カード不要。ブラウザの印刷機能でA4に出力できます。
          </p>
          <p style={{ margin: '10px auto 0', maxWidth: 720, fontSize: 11, color: '#94a3b8', lineHeight: 1.7 }}>
            ※ 掲示物は法令の周知を助けるものです。自社に必要な措置の詳細は、厚生労働省の資料および所轄労働基準監督署にご確認ください。
          </p>
        </div>
      </div>
    </section>
  );
};

export const LandingClosingCTA: React.FC<{ onTryDemo: () => void; onSignup: () => void }> = ({ onTryDemo, onSignup }) => (
  <section style={{ padding: 'clamp(20px,3vw,32px) clamp(16px,4vw,40px) clamp(48px,7vw,80px)', maxWidth: 1296, margin: '0 auto' }}>
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 24, background: 'linear-gradient(120deg,#ea580c,#f97316 55%,#eab308)', padding: 'clamp(36px,5vw,60px) clamp(24px,4vw,56px)', textAlign: 'center', boxShadow: '0 26px 50px -22px rgba(249,115,22,.6)' }}>
      <h2 style={{ margin: '0 0 12px', fontSize: 'clamp(1.5rem,3vw,2.3rem)', fontWeight: 800, color: '#fff', letterSpacing: '-.02em' }}>まずは30秒、登録なしで体感する</h2>
      <p style={{ margin: '0 0 26px', fontSize: 'clamp(.95rem,1.4vw,1.15rem)', color: 'rgba(255,255,255,.92)' }}>アプリ不要・カード不要。フリープランは¥0で今日から始められます。</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
        <button
          data-cta="hero-try-demo"
          onClick={onTryDemo}
          className="hover:-translate-y-0.5 transition"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 28px', borderRadius: 13, fontSize: 16, fontWeight: 800, color: '#ea580c', background: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 12px 24px -10px rgba(0,0,0,.3)' }}
        >
          今すぐ30秒で試す
        </button>
        <button
          data-cta="signup"
          onClick={onSignup}
          className="hover:bg-white/25 transition"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 26px', borderRadius: 13, fontSize: 16, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.4)', cursor: 'pointer' }}
        >
          無料で始める
        </button>
      </div>
    </div>
  </section>
);

interface ChangeItem { title: string }
const V1_ITEMS: ChangeItem[] = [
  { title: 'QR報告（アプリ不要・ログイン不要・30秒）' },
  { title: 'AIによる個人特定情報の自動匿名化' },
  { title: '4M分類・危険度判定・改善提案の自動生成' },
  { title: 'テキスト／音声／写真・多言語入力に対応' },
  { title: '管理ダッシュボード（4M別・危険度別の可視化）' },
];
const ROADMAP_ITEMS: ChangeItem[] = [
  { title: '業種別テンプレートの拡充（建設／製造／物流）' },
  { title: 'レポート出力・分析の強化' },
];

export const LandingChangelog: React.FC = () => (
  <section id="updates" style={{ padding: '0 clamp(16px,4vw,40px) clamp(44px,6vw,76px)', maxWidth: 1296, margin: '0 auto' }}>
    <div style={{ background: '#0f172a', borderRadius: 'clamp(20px,3vw,28px)', padding: 'clamp(32px,4vw,56px)' }}>
      <div style={{ maxWidth: 640, margin: '0 0 clamp(24px,3vw,36px)' }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', color: '#fdba74', textTransform: 'uppercase', marginBottom: 10 }}>更新履歴 / Changelog</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 'clamp(1.5rem,3vw,2.1rem)', fontWeight: 800, letterSpacing: '-.02em', color: '#fff' }}>プロダクトの歩み</h2>
        <p style={{ margin: 0, fontSize: 15, color: '#cbd5e1', lineHeight: 1.7 }}>安全ポスト＋は継続的に改善しています。主なリリース内容と今後の予定です。</p>
      </div>

      <div style={{ position: 'relative', paddingLeft: 26, borderLeft: '2px solid rgba(148,163,184,.25)', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* v1.0 */}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -34, top: 2, width: 14, height: 14, borderRadius: '50%', background: '#f97316', boxShadow: '0 0 0 4px rgba(249,115,22,.2)' }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>v1.0「現場の安全革命」</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#0f172a', background: '#34d399', borderRadius: 999, padding: '3px 10px' }}>リリース済み</span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '8px 20px' }}>
            {V1_ITEMS.map((it) => (
              <li key={it.title} style={{ display: 'flex', gap: 8, fontSize: 14, color: '#e2e8f0', lineHeight: 1.6 }}>
                <span style={{ color: '#34d399' }}>✓</span>{it.title}
              </li>
            ))}
          </ul>
        </div>

        {/* ロードマップ */}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -34, top: 2, width: 14, height: 14, borderRadius: '50%', background: '#0f172a', border: '2px solid #64748b' }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>これからの予定</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#cbd5e1', background: 'rgba(148,163,184,.2)', border: '1px solid rgba(148,163,184,.35)', borderRadius: 999, padding: '3px 10px' }}>ロードマップ</span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '8px 20px' }}>
            {ROADMAP_ITEMS.map((it) => (
              <li key={it.title} style={{ display: 'flex', gap: 8, fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>
                <span style={{ color: '#64748b' }}>○</span>{it.title}
              </li>
            ))}
          </ul>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#64748b' }}>※ 予定は検討中の内容であり、変更される場合があります。</p>
        </div>
      </div>
    </div>
  </section>
);
